import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';
import { styled, useTheme } from '@mui/material/styles';
import api from '../services/api';

import { Lock, Smartphone, Eye, Database } from 'lucide-react';
import Stack from '@mui/material/Stack';

const validationSchema = Yup.object({
  email: Yup.string().email('Indirizzo email non valido').required('Obbligatorio'),
});

const RootStyle = styled(Grid)(({ theme }) => ({
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': {
    minHeight: '100dvh',
  },
  overflowX: 'hidden',
}));

const ImageSection = styled(Grid)(({ theme }) => ({
  backgroundImage: 'url(https://source.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1920&q=80)', // Technology/Security
  backgroundRepeat: 'no-repeat',
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(30, 41, 59, 0.88) 100%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(10),
  color: theme.palette.common.white,
}));

const FeatureItem = ({ icon: Icon, text }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} color="#00AB55" />
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 500 }}>
      {text}
    </Typography>
  </Stack>
);

const FormSection = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: theme.spacing(2.5, 2, 4),
  [theme.breakpoints.up('md')]: {
    justifyContent: 'center',
    padding: theme.spacing(8, 4),
  },
}));

export default function ForgotPassword() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await api.post('/auth/forgot-password', { email: values.email });
        toast.success('Se l\'email esiste, riceverai un link per il reset');
        setEmailSent(true);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Errore durante la richiesta');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <RootStyle container component="main">
      <CssBaseline />
      <ImageSection item md={7} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Overlay>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: 'common.white' }}>
            Recupero Accesso
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 400, opacity: 0.8, mb: 8, maxWidth: 480 }}>
            Proteggiamo i tuoi dati sensibili con i più alti standard di sicurezza bancaria.
          </Typography>

          <Box>
            <FeatureItem icon={Lock} text="Crittografia End-to-End" />
            <FeatureItem icon={Smartphone} text="Autenticazione Sicura" />
            <FeatureItem icon={Eye} text="Monitoraggio Accessi Sospetti" />
            <FeatureItem icon={Database} text="Backup Giornalieri Automatici" />
          </Box>
        </Overlay>
      </ImageSection>
      <Grid item xs={12} sm={12} md={5} component={Paper} elevation={6} square sx={{ overflowY: 'auto' }}>
        <FormSection>
          <Box sx={{ mb: { xs: 1.5, md: 3 } }}>
            <img
              src="/logo.svg"
              alt="DB Logo"
              style={{
                width: 'clamp(96px, 24vw, 160px)',
                height: 'clamp(96px, 24vw, 160px)',
                filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
              }}
            />
          </Box>
          <Typography component="h1" variant="h5">
            Recupero Password
          </Typography>
          
          {emailSent ? (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Abbiamo inviato un link di reset all'indirizzo {formik.values.email}.
                Controlla la tua casella di posta (e lo spam).
              </Typography>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/login"
              >
                Torna al Login
              </Button>
            </Box>
          ) : (
            <Box component="form" noValidate onSubmit={formik.handleSubmit} sx={{ mt: 1, width: '100%', maxWidth: 400 }}>
              <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
                Inserisci la tua email e ti invieremo un link per reimpostare la password.
              </Typography>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Indirizzo Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia Link di Reset'}
              </Button>
              
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <Link component={RouterLink} to="/login" variant="body2">
                    Torna al Login
                  </Link>
                </Grid>
              </Grid>
            </Box>
          )}
        </FormSection>
      </Grid>
    </RootStyle>
  );
}
